import { invert, isNumber } from 'lodash';
import {
    AssessmentType,
    ExternalAssessmentStates
} from '../constants-types/Enums';
import { AssessmentReminderConfig } from '../core-data-model/AssessmentReminderConfig';
import { ArrayUtilities } from '../core-utilities/Array.utility';
import { DateFormatUtility } from '../core-utilities/DateFormat.utility';
import { ITranslationService } from '../system-interface/ITranslationService.interface';
import { Assessment } from './Assessment.model';
import { AssessmentMethods } from './AssessmentMethods';

export const COGNITIVE_SCORE_MIN = 100;
export const COGNITIVE_SCORE_MAX = 450;
export const COGNITIVE_SCORE_STEP = 10;
export const COGNITIVE_STANDARD_DEVIATION = 80;
export const COGNITIVE_SCORE_MEAN = 250;

// Scaled 0.0 - 6.0
export const standardDistributionPercentileToZScoreRange = 6;
export const standardDistributionPercentileToZScore: {
    [key: number]: number;
} = {
    0.01: 0.67,
    0.02: 0.95,
    0.03: 1.12,
    0.04: 1.25,
    0.05: 1.36,
    0.06: 1.45,
    0.07: 1.52,
    0.08: 1.59,
    0.09: 1.66,
    0.1: 1.72,
    0.11: 1.77,
    0.12: 1.83,
    0.13: 1.87,
    0.14: 1.92,
    0.15: 1.96,
    0.16: 2.01,
    0.17: 2.05,
    0.18: 2.08,
    0.19: 2.12,
    0.2: 2.16,
    0.21: 2.19,
    0.22: 2.23,
    0.23: 2.26,
    0.24: 2.29,
    0.25: 2.33,
    0.26: 2.36,
    0.27: 2.39,
    0.28: 2.42,
    0.29: 2.45,
    0.3: 2.48,
    0.31: 2.5,
    0.32: 2.53,
    0.33: 2.56,
    0.34: 2.59,
    0.35: 2.61,
    0.36: 2.64,
    0.37: 2.67,
    0.38: 2.69,
    0.39: 2.72,
    0.4: 2.75,
    0.41: 2.77,
    0.42: 2.8,
    0.43: 2.82,
    0.44: 2.85,
    0.45: 2.87,
    0.46: 2.9,
    0.47: 2.92,
    0.48: 2.95,
    0.49: 2.97,
    0.5: 3.0,
    0.51: 3.03,
    0.52: 3.05,
    0.53: 3.08,
    0.54: 3.1,
    0.55: 3.13,
    0.56: 3.15,
    0.57: 3.18,
    0.58: 3.2,
    0.59: 3.23,
    0.6: 3.25,
    0.61: 3.28,
    0.62: 3.31,
    0.63: 3.33,
    0.64: 3.36,
    0.65: 3.39,
    0.66: 3.41,
    0.67: 3.44,
    0.68: 3.47,
    0.69: 3.5,
    0.7: 3.52,
    0.71: 3.55,
    0.72: 3.58,
    0.73: 3.61,
    0.74: 3.64,
    0.75: 3.67,
    0.76: 3.71,
    0.77: 3.74,
    0.78: 3.77,
    0.79: 3.81,
    0.8: 3.84,
    0.81: 3.88,
    0.82: 3.92,
    0.83: 3.95,
    0.84: 3.99,
    0.85: 4.04,
    0.86: 4.08,
    0.87: 4.13,
    0.88: 4.17,
    0.89: 4.23,
    0.9: 4.28,
    0.91: 4.34,
    0.92: 4.41,
    0.93: 4.48,
    0.94: 4.55,
    0.95: 4.64,
    0.96: 4.75,
    0.97: 4.88,
    0.98: 5.05,
    0.99: 5.33
};

export const cognitiveScaledScoreToPercentileMap: {
    [key: number]: number;
} = {
    100: 1,
    110: 2,
    120: 3,
    130: 4,
    140: 6,
    150: 8,
    160: 10,
    170: 14,
    180: 17,
    190: 21,
    200: 26,
    210: 31,
    220: 36,
    230: 41,
    240: 46,
    250: 52,
    260: 57,
    270: 61,
    280: 66,
    290: 70,
    300: 74,
    310: 77,
    320: 81,
    330: 83,
    340: 86,
    350: 88,
    360: 90,
    370: 92,
    380: 93,
    390: 94,
    400: 95,
    410: 96,
    420: 97,
    430: 98,
    440: 98,
    450: 99
};

export const cognitiveScaledScoreToPercentileMapInverted = invert(
    cognitiveScaledScoreToPercentileMap
);

export class CognitiveAssessment implements Assessment {
    readonly administeredBy: string;
    readonly administeredById: string;
    readonly assessmentState?: ExternalAssessmentStates;
    readonly id: string;
    readonly completedDate: Date;
    readonly sentDate: Date;
    readonly type?: AssessmentType = AssessmentType.Cognitive;
    readonly score: number;
    readonly scoreWeight?: number;

    // metadata info
    readonly attempted: number;
    readonly correct: number;
    readonly percentileRank: number;

    constructor(startingValues?: Partial<CognitiveAssessment>) {
        Object.assign(this, startingValues);
    }

    /**
     * Find the closest matching score from the given percentile
     *
     * @param percentile
     */
    static getScoreFromPercentile(percentile: number): number {
        const percentiles = Object.values(cognitiveScaledScoreToPercentileMap);
        const matchedPercentage = ArrayUtilities.getClosestValue(
            percentiles,
            percentile,
            (a, b) => Math.abs(a - b)
        );
        return parseInt(
            cognitiveScaledScoreToPercentileMapInverted[matchedPercentage],
            10
        );
    }

    /**
     * Find the closes matching percentile from the given score
     *
     * @param score
     */
    static getPercentileFromScore(score: number): number {
        const closestScore = this.getClosestScore(score);
        return cognitiveScaledScoreToPercentileMap[closestScore];
    }

    /**
     * Find the closest valid score for a given (possibly invalid) score
     *
     * @param score
     */
    static getClosestScore(score: number): number {
        const scores = Object.keys(cognitiveScaledScoreToPercentileMap).map(
            (k) => parseInt(k, 10)
        );
        const closestScore = ArrayUtilities.getClosestValue(
            scores,
            score,
            (a, b) => Math.abs(a - b)
        );
        return closestScore;
    }

    isCompleted(): boolean {
        return this.assessmentState === ExternalAssessmentStates.Completed;
    }
    isPending(): boolean {
        return this.assessmentState === ExternalAssessmentStates.Pending;
    }
    isExpired(): boolean {
        return this.assessmentState === ExternalAssessmentStates.Expired;
    }
    isPendingOrExpired(): boolean {
        return this.isPending() || this.isExpired();
    }
    isPendingExpiredOrAborted(): boolean {
        return this.isPendingOrExpired() || this.isAborted();
    }
    isAborted(): boolean {
        return this.assessmentState === ExternalAssessmentStates.Aborted;
    }

    getReminderConfig(
        personId: string,
        lastSentDate: Date,
        TR: ITranslationService,
        dateFormat: DateFormatUtility,
        assessmentId?: string
    ): AssessmentReminderConfig {
        return AssessmentMethods.getReminderConfig(
            this,
            AssessmentType.Cognitive,
            personId,
            lastSentDate,
            TR,
            dateFormat,
            assessmentId
        );
    }

    getReminderDescription(date: DateFormatUtility, TR: ITranslationService) {
        return AssessmentMethods.getReminderDescription(this, date, TR);
    }

    hasScore(): boolean {
        return isNumber(this.score);
    }
}
